/**
 * オートエイム（自動照準）の純幾何計算。GPL 非依存・描画/入力非依存の純関数群。
 *
 * 各ポケットを「ポケットインできる的玉進入角の窓」として扱う（窓は開口を挟む2ジョーを
 * `R+ジョー半径` で膨らませた円への接線で定義され、浅い進入角ほど狭くなる＝実効開口幅が
 * 進入角で変わる）。窓の中で:
 *   - 理想狙点 = 窓の中央（ジョーから最も離れる＝クリアランス最大）を最優先
 *   - 手前に障害球がある場合は、窓内で障害を避けられる角度のうち理想に最も近いものを採用
 *     （ジョーぎりぎりの角度も許容＝ポケット可能角度の中での優先順位）
 * ポケット間の優先順位:
 *   - カットが緩い（厚い）方を優先（score=カット角）
 *   - 距離が近いサイドポケットはコーナーより優先（近サイドボーナス）
 *
 * 撞点・強さによる初速偏向（スクワート/スワーブ/スロー）の補正は呼び出し側（controls）が
 * 実物理シミュレート（world.simulateObjectBallDir）で行う。本モジュールは純幾何で、的玉が
 * 出るべき方向 objDir と、その初期推定となる手玉進路 travelAngle を返す。
 */

export interface Vec2 {
  readonly x: number;
  readonly y: number;
}

/** ポケット狙点＋ジョー（開口を挟むナックル）。 */
export interface AutoAimPocket {
  /** ポケット中心。 */
  readonly x: number;
  readonly y: number;
  /** サイドポケットか（近距離時にコーナーより優先する）。 */
  readonly isSide?: boolean;
  /** 開口を挟む2ナックル（中心＋半径）。窓（ポケット可能角度）の算出に使う。 */
  readonly jaws?: ReadonlyArray<{ x: number; y: number; r: number }>;
}

export interface AutoAimInput {
  /** 手玉中心。 */
  readonly cue: Vec2;
  /** 狙う的玉中心。 */
  readonly target: Vec2;
  /** 障害になりうる他球の中心（手玉・的玉は除外して渡すこと）。 */
  readonly obstacles: readonly Vec2[];
  /** ポケット狙点（＋任意でジョー・isSide）の一覧。 */
  readonly pockets: readonly AutoAimPocket[];
  /** 球半径 [m]。 */
  readonly ballR: number;
  /** 許容カット角の上限 [rad]（ほぼ90°＝裏回り込みのみ除外）。 */
  readonly maxCut: number;
  /** この距離以下のサイドポケットはコーナーより優先する [m]（任意）。 */
  readonly sideNearDist?: number;
}

export interface AutoAimSolution {
  /** 手玉の必要進路角 [rad]（cue→ghost・スクワート逆補正前の初期推定）。 */
  readonly travelAngle: number;
  /** 的玉が出ていくべき方向 [rad]（実シミュ逆算の目標）。 */
  readonly objDir: number;
  /** 手玉が接触（ゴースト位置）まで走る距離 [m]。代表速度の見積りに使う。 */
  readonly contactDist: number;
  /** 採用したポケットの index。 */
  readonly pocketIndex: number;
  /** カット角 [rad]（小さいほど厚い）。 */
  readonly cutAngle: number;
}

/** 近サイドポケットをコーナーより優先する「厚み換算」ボーナス [rad]。 */
const SIDE_NEAR_BONUS = 0.3;
/** 窓端（ジョー接線ぎりぎり）を避けるための内側マージン比（窓幅に対する割合）。 */
const WINDOW_INSET = 0.05;
/** 窓内サンプル数（理想に近い順に障害クリアを探す）。 */
const WINDOW_SAMPLES = 9;

const TWO_PI = Math.PI * 2;
/** 角度を [-π,π] に正規化。 */
const norm = (a: number): number => {
  let x = a % TWO_PI;
  if (x > Math.PI) x -= TWO_PI;
  if (x < -Math.PI) x += TWO_PI;
  return x;
};

/** 点 (px,py) から線分 (ax,ay)-(bx,by) までの最短距離 [m]。 */
const distPointToSeg = (
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number => {
  const vx = bx - ax;
  const vy = by - ay;
  const len2 = vx * vx + vy * vy;
  let t = len2 > 0 ? ((px - ax) * vx + (py - ay) * vy) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + vx * t), py - (ay + vy * t));
};

/** 半径 ballR の球が通る線分が障害球（半径 ballR）と干渉しないか（中心間 < 2R で塞がり）。 */
const pathClear = (
  ax: number,
  ay: number,
  bx: number,
  by: number,
  obstacles: readonly Vec2[],
  minDist: number,
): boolean => {
  for (const o of obstacles) {
    if (distPointToSeg(o.x, o.y, ax, ay, bx, by) < minDist) return false;
  }
  return true;
};

/**
 * ポケット pk について、的玉進入角の窓 [lo,hi]（絶対角 [rad]）を返す。ジョーが2つ無い／
 * 距離が近すぎてジョーに食い込む／窓が成立しない場合は null（＝中心狙いのみ扱い側で対応）。
 */
const pocketWindow = (
  target: Vec2,
  pk: AutoAimPocket,
  aC: number,
  ballR: number,
): { lo: number; hi: number } | null => {
  if (!pk.jaws || pk.jaws.length !== 2) return null;
  const edges: { aj: number; phi: number; rel: number }[] = [];
  for (const j of pk.jaws) {
    const jx = j.x - target.x;
    const jy = j.y - target.y;
    const dj = Math.hypot(jx, jy);
    const eff = ballR + j.r;
    if (dj <= eff) return null; // ジョーに食い込む距離＝窓計算不能
    const aj = Math.atan2(jy, jx);
    edges.push({ aj, phi: Math.asin(eff / dj), rel: norm(aj - aC) });
  }
  const lower = edges[0]!.rel < edges[1]!.rel ? edges[0]! : edges[1]!;
  const upper = edges[0]!.rel < edges[1]!.rel ? edges[1]! : edges[0]!;
  const lo = lower.aj + lower.phi; // 下側ジョーの内側エッジ
  const hi = upper.aj - upper.phi; // 上側ジョーの内側エッジ
  if (norm(hi - lo) <= 0) return null; // 窓なし（直接は入らない）
  return { lo, hi };
};

/**
 * 1 ポケット分のオートエイム解を求める内部関数。`solveAutoAim` と `findMakeablePockets` が共有。
 * 経路クリア & カット角 ≤ maxCut の解が見つかれば返し、見つからなければ null。
 */
const solveSinglePocket = (
  cue: Vec2,
  target: Vec2,
  obstacles: readonly Vec2[],
  pk: AutoAimPocket,
  pocketIndex: number,
  ballR: number,
  maxCut: number,
): AutoAimSolution | null => {
  const twoR = 2 * ballR;
  const dcx = pk.x - target.x;
  const dcy = pk.y - target.y;
  const distToPocket = Math.hypot(dcx, dcy);
  if (distToPocket < 1e-6) return null;
  const aC = Math.atan2(dcy, dcx);

  const samples: number[] = [];
  const hasJaws = !!pk.jaws && pk.jaws.length === 2;
  if (hasJaws) {
    const win = pocketWindow(target, pk, aC, ballR);
    if (!win) return null;
    const span = norm(win.hi - win.lo);
    const mid = win.lo + span / 2;
    for (let s = 0; s < WINDOW_SAMPLES; s++) {
      const t = WINDOW_INSET + (1 - 2 * WINDOW_INSET) * (s / (WINDOW_SAMPLES - 1));
      samples.push(win.lo + span * t);
    }
    samples.sort((a, b) => Math.abs(norm(a - mid)) - Math.abs(norm(b - mid)));
  } else {
    samples.push(aC);
  }

  for (const theta of samples) {
    const ux = Math.cos(theta);
    const uy = Math.sin(theta);
    const ex = target.x + ux * distToPocket;
    const ey = target.y + uy * distToPocket;
    if (!pathClear(target.x, target.y, ex, ey, obstacles, twoR)) continue;
    const gx = target.x - ux * twoR;
    const gy = target.y - uy * twoR;
    if (!pathClear(cue.x, cue.y, gx, gy, obstacles, twoR)) continue;
    const cgx = gx - cue.x;
    const cgy = gy - cue.y;
    const cgLen = Math.hypot(cgx, cgy);
    if (cgLen < 1e-6) continue;
    const travelAngle = Math.atan2(cgy, cgx);
    const cut = Math.abs(norm(travelAngle - theta));
    if (cut > maxCut) continue;
    return {
      travelAngle,
      objDir: theta,
      contactDist: cgLen,
      pocketIndex,
      cutAngle: cut,
    };
  }
  return null;
};

/**
 * 経路が通り cutAngle ≤ maxCut を満たす全ポケットの解を返す。空配列なら狙えるポケットなし。
 * UI でポケット候補のグロー表示・タップ切替に使う（最厚を選ばず、利用側が任意に選択する想定）。
 */
export const findMakeablePockets = (
  input: AutoAimInput,
): AutoAimSolution[] => {
  const { cue, target, obstacles, pockets, ballR, maxCut } = input;
  if (!(ballR > 0) || pockets.length === 0) return [];
  const out: AutoAimSolution[] = [];
  for (let i = 0; i < pockets.length; i++) {
    const sol = solveSinglePocket(
      cue,
      target,
      obstacles,
      pockets[i]!,
      i,
      ballR,
      maxCut,
    );
    if (sol) out.push(sol);
  }
  return out;
};

/**
 * オートエイムの幾何解を求める。候補が無ければ null（＝自動照準は発動しない）。
 */
export const solveAutoAim = (input: AutoAimInput): AutoAimSolution | null => {
  const { cue, target, obstacles, pockets, ballR, maxCut, sideNearDist } = input;
  if (!(ballR > 0) || pockets.length === 0) return null;
  const twoR = 2 * ballR;

  let best: AutoAimSolution | null = null;
  let bestScore = Infinity;

  for (let i = 0; i < pockets.length; i++) {
    const pk = pockets[i]!;
    const dcx = pk.x - target.x;
    const dcy = pk.y - target.y;
    const distToPocket = Math.hypot(dcx, dcy);
    if (distToPocket < 1e-6) continue;
    const aC = Math.atan2(dcy, dcx);

    // ポケット可能角度の窓。ジョーが無ければ中心方向のみ（点ポケット扱い）。
    const samples: number[] = [];
    const hasJaws = !!pk.jaws && pk.jaws.length === 2;
    if (hasJaws) {
      const win = pocketWindow(target, pk, aC, ballR);
      if (!win) continue; // ジョーありで窓が成立しない＝直接は入らない → 不採用
      const span = norm(win.hi - win.lo);
      const mid = win.lo + span / 2;
      for (let s = 0; s < WINDOW_SAMPLES; s++) {
        const t =
          WINDOW_INSET + (1 - 2 * WINDOW_INSET) * (s / (WINDOW_SAMPLES - 1));
        samples.push(win.lo + span * t);
      }
      // 理想（窓中央）に近い順に評価し、障害をクリアする最初の角度を採る。
      samples.sort((a, b) => Math.abs(norm(a - mid)) - Math.abs(norm(b - mid)));
    } else {
      samples.push(aC);
    }

    let chosen: { theta: number; travelAngle: number; contactDist: number; cut: number } | null =
      null;
    for (const theta of samples) {
      const ux = Math.cos(theta);
      const uy = Math.sin(theta);
      // 的玉経路 T → ポケット入口（中心距離まで）。
      const ex = target.x + ux * distToPocket;
      const ey = target.y + uy * distToPocket;
      if (!pathClear(target.x, target.y, ex, ey, obstacles, twoR)) continue;
      // ゴースト（接触時の手玉中心）と cue→ghost 経路。
      const gx = target.x - ux * twoR;
      const gy = target.y - uy * twoR;
      if (!pathClear(cue.x, cue.y, gx, gy, obstacles, twoR)) continue;
      const cgx = gx - cue.x;
      const cgy = gy - cue.y;
      const cgLen = Math.hypot(cgx, cgy);
      if (cgLen < 1e-6) continue;
      const travelAngle = Math.atan2(cgy, cgx);
      const cut = Math.abs(norm(travelAngle - theta));
      if (cut > maxCut) continue;
      chosen = { theta, travelAngle, contactDist: cgLen, cut };
      break;
    }
    if (!chosen) continue;

    // スコア = カット角（厚い優先）− 近サイドボーナス。
    let score = chosen.cut;
    if (pk.isSide && sideNearDist != null && distToPocket <= sideNearDist) {
      score -= SIDE_NEAR_BONUS;
    }
    if (best === null || score < bestScore) {
      bestScore = score;
      best = {
        travelAngle: chosen.travelAngle,
        objDir: chosen.theta,
        contactDist: chosen.contactDist,
        pocketIndex: i,
        cutAngle: chosen.cut,
      };
    }
  }
  return best;
};
